/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import qs from "qs";
import { useLazyQuery, useMutation } from "@apollo/client";
import md5 from "blueimp-md5";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { SelfObject, RootState, DisplayedSummitView } from "store/types";

import { Alert, useTimerRef } from "UpduoLibrary";
import {
  addPubNubSubscribedMessage,
  clearPubNubSubscribedMessages,
  initializePubNub,
  // recoverPubNubEvent,
} from "config/UpduoPubNub";
import { requestSessionPermissions } from "config/UpduoPermissions";
import { SkalePrimaryApolloClient } from "config/UpduoApolloClient";
import { UpduoSounds } from "config/UpduoSounds";
import {
  homeResetState,
  startResetState,
  chatResetState,
  instantiateMemberUser,
} from "config/ResetState";
import { callApi, showDisconnectedAlert } from "config/UpduoService";
import { processSkillString } from "config/Utilities";
import { showUpdateAlert } from "config/UpduoUpdate";
import Summit from "./Summit";
import Match from "./Match";
import DeviceSwitch from "./DeviceSwitch";
import Chat from "./Chat";
import SessionFinish from "./SessionFinish";
import {
  DECLARE_MATCH_CANCEL,
  PRIMARY_JOIN_ORG,
  REPORT_SESSION_PROBLEM,
} from "graphql/internal/Mutations";
import { GET_SKALESESSION } from "graphql/internal/Queries";
import {
  DeclareMatchCancelResponse,
  GetSkaleSessionQuery,
  PrimaryJoinOrgResponse,
  ReportSessionProblemResponse,
  CancelReasonEnum,
} from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function SummitRoute({ match, history }) {
  const dispatch = useDispatch();
  const location = useLocation();

  const displayedSummitView = useSelector(
    (state: RootState) => state.displayedSummitView
  );
  const displayedSummitViewRef =
    useRef<DisplayedSummitView>(displayedSummitView);
  displayedSummitViewRef.current = displayedSummitView;

  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const authToken = useSelector((state: RootState) => state.authToken);
  const chatFailed = useSelector((state: RootState) => state.chatFailed);
  const chatConnected = useSelector((state: RootState) => state.chatConnected);
  const chatPartnerHasCancelled = useSelector(
    (state: RootState) => state.chatPartnerHasCancelled
  );
  const matchObject = useSelector((state: RootState) => state.matchObject);
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const selfObjectRef = useRef<SelfObject>();
  selfObjectRef.current = selfObject;

  const isQueueBeaconActivated = useSelector(
    (state: RootState) => state.isQueueBeaconActivated
  );
  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );

  const [queueInProgress, setQueueInProgress] = useState(
    false || isQueueBeaconActivated
  );
  const [preProcessingInProgress, setPreProcessingInProgress] = useState(true);
  const preProcessingInProgressRef = useRef<boolean>();
  preProcessingInProgressRef.current = preProcessingInProgress;

  const [postProcessingInProgress, setPostProcessingInProgress] = useState(
    chatFailed || !chatPartnerHasCancelled
  );
  const [matchStatusInProgress, setMatchStatusInProgress] = useState(false);

  const [startSessionButtonPressed, setStartSessionButtonPressed] =
    useState(false);
  const startSessionButtonPressedRef = useRef<boolean>();
  startSessionButtonPressedRef.current = startSessionButtonPressed;

  const [alreadyInQueue, setAlreadyInQueue] = useState(false);
  const [matchStatusObject, setMatchStatusObject] = useState<{
    request: string;
    payload?: any;
  } | null>(null);
  const [queueCheckCount, setQueueCheckCount] = useState(0);

  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);

  const [setQueueCheckTimer, clearQueueCheckTimer] = useTimerRef();
  const [setChatDisplayedSummitViewTimer, clearChatDisplayedSummitViewTimer] =
    useTimerRef();
  // const [setMatchAckTimer, clearMatchAckTimer] = useTimerRef();
  // const [setMatchFoundTimer, clearMatchFoundTimer] = useTimerRef();
  // TODO: Add timers for match-cancel and match-find

  // TODO: this whole query should be revisited. I don't think we need to
  // manually construct this data and store it in redux like this. We can
  // probably just use the actual queried data and pass it directly to the
  // components that use it
  const [getSkaleSession] = useLazyQuery<GetSkaleSessionQuery>(
    GET_SKALESESSION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data: GetSkaleSessionQuery) => {
        const session = data?.skalesession;
        if (session?.agoraChannel) {
          const partnerIsTeacher = matchObject.partnerId === session.teacher.id;
          const newPartnerObject = Object.assign(
            {},
            partnerIsTeacher ? session.teacher : session.learner
          );
          dispatch(ActionCreators.setPartnerObject(newPartnerObject));
          dispatch(
            ActionCreators.setSkaleSessionObject({
              id: session.id,
              summitId: session?.summit?.id,
              teacherId: session.teacher.id,
              learnerId: session.learner.id,
              skillId: session.skill.id,
              agoraChannel: session.agoraChannel,
              isTeacherReady: session.isTeacherReady,
              isLearnerReady: session.isLearnerReady,
              hasTeacherSeenContent: session.hasTeacherSeenContent,
              hasLearnerSeenContent: session.hasLearnerSeenContent,
              stepsReadySelf:
                session[
                  partnerIsTeacher ? "stepsReadyLearner" : "stepsReadyTeacher"
                ],
              stepsReadyPartner:
                session[
                  partnerIsTeacher ? "stepsReadyTeacher" : "stepsReadyLearner"
                ],
            })
          );
          const sessionSteps = JSON.parse(session.skill.sessionSteps);
          const newSkillObject = {
            ...session.skill,
            sessionSteps,
          };
          // Perform string replacements for all relevant step card fields
          const roles = ["teacher", "learner"];
          const stringTypes = ["heading", "detail"];
          for (let i = 0; i < newSkillObject.sessionSteps.length; i++) {
            for (const role of roles) {
              for (const stringType of stringTypes) {
                newSkillObject.sessionSteps[i][role][stringType] =
                  processSkillString(
                    newSkillObject.sessionSteps[i][role][stringType],
                    session.teacher.firstName,
                    session.learner.firstName,
                    session.skill.titleTeacher,
                    session.skill.titleLearner,
                    session.skill.name,
                    role === "teacher",
                    selfObject.organizationName
                  );
              }
            }
          }
          dispatch(ActionCreators.setSkillObject(newSkillObject));
          _handleClearQueueCheckTimer();
          setChatDisplayedSummitViewTimer(
            setTimeout(() => {
              dispatch(ActionCreators.setDisplayedSummitView("Chat"));
            }, 1500)
          );
        }
      },
      onError: () => {
        UpduoAnalytics.logEvent("app-match-cancel", {
          reason: "match-fail",
        });
        _handleRestoreHome();
      },
    }
  );

  const [primaryJoinOrg, {}] = useMutation<PrimaryJoinOrgResponse>(
    PRIMARY_JOIN_ORG,
    {
      fetchPolicy: "no-cache", // this line is needed in order to proceed
      client: SkalePrimaryApolloClient,
      onCompleted: ({ ok, reason }) => {
        if (!ok && reason) {
          history.go(0);
          return;
        } else {
          if (match.params.inviteUuid) {
            history.push({
              pathname: location.pathname,
              search: "?rsvpSuccess=true",
            });
          } else history.go(0);
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-summit-primaryjoinorg-error", {
          error,
        });
        console.error("ERROR primaryJoinOrg", error);
      },
    }
  );

  const [declareMatchCancel, {}] = useMutation<DeclareMatchCancelResponse>(
    DECLARE_MATCH_CANCEL,
    {}
  );

  const [reportSessionProblem, {}] = useMutation<ReportSessionProblemResponse>(
    REPORT_SESSION_PROBLEM,
    {
      onCompleted: () => {
        UpduoAnalytics.logEvent("app-report-other-chat-failed");
        setPostProcessingInProgress(false);
      },
    }
  );

  useEffect(() => {
    if (displayedSummitView === "Match" && queueInProgress) {
      dispatch(ActionCreators.setAutoJoinSummit(false));
    }
  }, [displayedSummitView, queueInProgress]);

  useEffect(() => {
    if (primaryAuthToken) {
      instantiateMemberUser(() => {
        // This method contains full execution path for initiating Home screen
        // Show alert, if applicable, after a delay to allow component to fully render
        if (chatFailed) {
          dispatch(ActionCreators.setChatAgendaModalVisible(false));
          // Post session problem to the server
          if (matchObject.skalesessionId) {
            reportSessionProblem({
              variables: {
                reportSessionProblemRequest: {
                  skalesessionId: matchObject.skalesessionId,
                  reason: "Agora grace period expired",
                },
              },
            });
          }
          _handleDisplaySimpleAlert("CHAT_FAILED");
        } else if (!chatPartnerHasCancelled) {
          // Show alert, after a delay to allow component to fully render
          dispatch(ActionCreators.setChatAgendaModalVisible(false));
          UpduoAnalytics.logEvent("app-report-other-chat-cancelled");
          _handleDisplaySimpleAlert("CANCEL");
        }
        chatResetState();
      });
    }
    return () => {
      clearChatDisplayedSummitViewTimer();
      clearQueueCheckTimer();
      // clearMatchAckTimer();
      // clearMatchFoundTimer();
    };
  }, [dispatch]);

  useEffect(() => {
    // Allow IN_QUEUE status to trigger necessary client-side setup
    if (alreadyInQueue) {
      _handleStartSessionButtonPress();
    }
  }, [alreadyInQueue]);

  useEffect(() => {
    // Creation of matchStatusObject triggers establishment of PubNub event listeners
    if (matchStatusObject) {
      // TODO: bring these PubNub events' recovery back after we get ability to filter out irrelevant ones
      // setMatchAckTimer(
      //   setInterval(() => {
      //     recoverPubNubEvent("match-ack", (recentMessages) => {
      //       _handleMatchAckResponse(recentMessages[0]["ack_id"]);
      //     });
      //   }, 5000)
      // );
      // setMatchFoundTimer(
      //   setInterval(() => {
      //     recoverPubNubEvent("match-found", (recentMessages) => {
      //       _handleMatchFoundResponse(recentMessages[0]);
      //     });
      //   }, 5000)
      // );
      // Begin listening for all PubNub events required for starting a Skale Session
      addPubNubSubscribedMessage(
        "match-ack",
        (remoteMessage: { ack_id: string }) => {
          // Basically we're using the match-ack event as our cue to ask for queue status
          // (Only do this on Match view; being on Home indicates that user doesn't want a match)
          _handleMatchAckResponse(remoteMessage.ack_id);
        }
      );
      addPubNubSubscribedMessage(
        "match-cancel",
        (remoteMessage: { reason: string }) => {
          if (remoteMessage.reason === "TIMEOUT") {
            _handleDisplaySimpleAlert("SLOW");
          } else if (remoteMessage.reason === "DECLINE") {
            _handleDisplaySimpleAlert("DECLINE");
          }
          UpduoAnalytics.logEvent("app-match-cancel", remoteMessage);
          // Need to do this workaround for when Android returns from background
          // (See _handleAppStateChange code block for continuation)
          chatResetState();
          _handleRestoreHome();
        }
      );
      addPubNubSubscribedMessage("match-find", () => {
        // The 'match-find' event means we're in the queue,
        // or we've been reinserted after previous partner failed to ack,
        // so we toggle on availability
        dispatch(ActionCreators.setMatchAck(false));
        setStartSessionButtonPressed(true);
        UpduoAnalytics.logEvent("app-match-find");
      });
      addPubNubSubscribedMessage("match-found", (remoteMessage) => {
        _handleMatchFoundResponse(remoteMessage);
      });
      addPubNubSubscribedMessage("active-device-switch", (remoteMessage) => {
        const deviceId = `web_${md5(window.navigator)}`;
        if (deviceId !== remoteMessage.device_id) {
          // User is now using Skale on another device
          _handleSetInactiveDevice();
          UpduoAnalytics.logEvent("app-active-device-switch", remoteMessage);
        }
      });
      switch (matchStatusObject.request) {
        case "match":
          _handlePrepareForMatchQueue();
          break;
        case "match-ack":
          _handleMatchAckResponse(matchStatusObject.payload.ack_id);
          break;
        case "match-found":
          _handleMatchFoundResponse(matchStatusObject.payload);
          break;
        default:
          // Just set up PubNub listeners, take no action here
          setPostProcessingInProgress(false);
      }
      setMatchStatusObject(null);
    }
  }, [matchStatusObject, dispatch]);

  useEffect(() => {
    // Check if local match state agrees with server every 30 seconds when on Match screen
    if (
      queueCheckCount > 0 &&
      startSessionButtonPressed &&
      displayedSummitView === "Match"
    ) {
      if (queueCheckCount > 1) {
        _handleMatchStatusQueueCheck();
      }
      setQueueCheckTimer(
        setTimeout(() => {
          setQueueCheckCount((previousState) => previousState + 1);
        }, 30000)
      );
    } else {
      _handleClearQueueCheckTimer();
    }
  }, [queueCheckCount]);

  useEffect(() => {
    if (displayedSummitView !== "Match") {
      _handleRestoreHome(displayedSummitView === "Summit");
    }
    if (primaryAuthToken && selfObject.userId !== currentMemberId) {
      // Initialize PubNub only when the member switches,
      // not when selfObject is simply updated while in the same organization
      initializePubNub(() => {
        addPubNubSubscribedMessage("me-update", () => {
          // Cue update of selfObject
          instantiateMemberUser();
        });
        addPubNubSubscribedMessage(
          "announcement",
          (remoteMessage: { heading: string; content: string }) => {
            // Show announcement
            dispatch(ActionCreators.setAnnouncementObject(remoteMessage));
          }
        );
        _handleMatchStatus();
      });
      setCurrentMemberId(selfObject.userId);
    }
  }, [selfObject]);

  useEffect(() => {
    if (isQueueBeaconActivated) {
      _handleMatchStatus();
    }
  }, [isQueueBeaconActivated]);

  useEffect(() => {
    if (displayedSummitView === "Summit") {
      setStartSessionButtonPressed(false);
    }
  }, [displayedSummitView]);

  const _handleClearQueueCheckTimer = () => {
    setQueueCheckCount(0);
    clearQueueCheckTimer();
  };

  const _handleResetStartSessionButton = () => {
    setPostProcessingInProgress(false);
    setStartSessionButtonPressed(false);
  };

  const _handleStartSessionButtonPress = useCallback(() => {
    // Code path to execute following user tapping Start Session button
    // Turn on in-progress visual
    setPostProcessingInProgress(!alreadyInQueue);
    // Switch availability toggle on for user to see
    UpduoAnalytics.logEvent("user-toggle-on");
    // Now startResetState/chatResetState for aesthetics during nav reset
    startResetState();
    setMatchStatusObject({ request: "match" });
  }, [alreadyInQueue]);

  const _handleMatchAckResponse = useCallback(
    (ackId: string) => {
      // Triggers when app realizes server is expecting an ack response
      // (and deliver the ack promptly)
      // Post a match ack ID to signal readiness to receive match-found
      // clearMatchAckTimer();
      dispatch(ActionCreators.setChatAgendaModalVisible(true));
      dispatch(ActionCreators.setDisplayedSummitView("Match"));
      callApi("postMatchAck", { ack_id: ackId });
      dispatch(ActionCreators.setMatchAck(true));
      // Successfully connected, so we can reset trainer beacon
      dispatch(ActionCreators.setIsQueueBeaconActivated(false));
      // Play the match sound
      UpduoSounds.match.play();
      setPostProcessingInProgress(false);
      UpduoAnalytics.logEvent("app-match-ack", { ack_id: ackId });
    },
    [dispatch]
  );

  const _handleResetQueue = useCallback(() => {
    // Reset to un-queued state
    clearPubNubSubscribedMessages([
      "announcement",
      "me-update",
      "summit-update",
    ]);
    setQueueInProgress(false);
    setPreProcessingInProgress(false);
    setPostProcessingInProgress(false);
    _handleRestoreHome();
    _handleClearQueueCheckTimer();
    dispatch(ActionCreators.setMatchAck(false));
    if (displayedSummitViewRef.current !== "Summit") {
      dispatch(ActionCreators.setDisplayedSummitView("Summit"));
    }
  }, [_handleClearQueueCheckTimer, dispatch]);

  const _handleFailedMatchApi = useCallback(() => {
    // Reset and show alert when API fails due to internet failure during the match process
    _handleResetQueue();
    showDisconnectedAlert();
  }, [_handleResetQueue]);

  const _handleNotInQueue = useCallback(() => {
    // If toggled on but user is not in queue, switch toggle off (unless user pressed during pre-processing)
    if (startSessionButtonPressedRef.current) {
      setPreProcessingInProgress(false);
      _handleStartSessionButtonPress();
    } else {
      // Reset queueing to a good state
      _handleResetQueue();
    }
  }, [_handleResetQueue, _handleStartSessionButtonPress]);

  const _handleUserMatchCancel = useCallback(() => {
    // Safely remove user from server's session queue
    UpduoAnalytics.logEvent("user-toggle-off");
    // Post a match cancellation request to the server, if Skale session object is created
    declareMatchCancel({
      variables: {
        declareMatchCancelRequest: {
          cancelledReason: CancelReasonEnum.LeavingQueue,
        },
      },
    });
    // Delete the user from the match queue
    _handleResetQueue();
    setPostProcessingInProgress(false);
    history.push("/");
  }, [matchObject.skalesessionId, _handleResetQueue, selectedSummit]);

  const _handleRestoreHome = (isAlreadyHome = false) => {
    // Restore Home screen to normal
    _handleResetStartSessionButton();
    homeResetState(isAlreadyHome);
  };

  const _handleMatchFoundResponse = useCallback(
    (matchPayload: {
      agora_channel: string;
      agora_token: string;
      skalesession_id: string;
      partner_id: string;
    }) => {
      // clearMatchFoundTimer();
      if (displayedSummitView !== "Chat") {
        dispatch(ActionCreators.setMatchAck(true));
      }
      _handleClearQueueCheckTimer();
      setPostProcessingInProgress(false);
      // Retrieve partner, Skale session, and skill info synchronously before going to ChatScreen
      dispatch(
        ActionCreators.setMatchObject({
          agoraChannel: matchPayload.agora_channel,
          agoraToken: matchPayload.agora_token,
          skalesessionId: matchPayload.skalesession_id,
          partnerId: matchPayload.partner_id,
        })
      );
      getSkaleSession({
        variables: {
          getSkaleSessionDetailRequest: {
            skalesessionId: matchPayload.skalesession_id,
          },
        },
      });
      UpduoAnalytics.logEvent("app-match-found", matchPayload);
    },
    [displayedSummitView, _handleClearQueueCheckTimer, dispatch]
  );

  const _handleRecoverWhenNotQueued = useCallback(
    (responseObject: {
      title?: string;
      message?: string;
      outdated?: boolean;
    }) => {
      // Return to sane state if server rejects a queue request
      _handleRestoreHome();
      _handleUserMatchCancel();
      UpduoAnalytics.logEvent("app-queued-fail", {
        title: responseObject.title,
        message: responseObject.message,
      });
    },
    [_handleRestoreHome]
  );

  const _handleQueueFailedOutdated = useCallback(
    (responseObject: {
      title?: string;
      message?: string;
      outdated?: boolean;
    }) => {
      // Return the app to a good state if queue request fails
      _handleRecoverWhenNotQueued(responseObject);
      showUpdateAlert(responseObject);
    },
    [_handleRecoverWhenNotQueued]
  );

  const _handleQueueFailedClosed = useCallback(
    (responseObject: { title?: string; message?: string }) => {
      // Return the app to a good state if queue is closed
      if (responseObject.title && responseObject.message) {
        _handleRecoverWhenNotQueued(responseObject);
        history.push("/");
        Alert(responseObject.title, responseObject.message, []);
      }
    },
    [_handleRecoverWhenNotQueued]
  );

  const _handleDisplaySimpleAlert = useCallback(
    (scenario: string) => {
      // Show relevant informative alert dialogs when certain scenarios arise
      let title: string;
      let subtitle: string;
      switch (scenario) {
        case "SLOW":
          title = "You did not connect with your partner in time.";
          subtitle = "No worries -- please try again whenever you're ready!";
          break;
        case "CANCEL":
          title = "Session Ended";
          subtitle = "Your partner had to cancel.";
          break;
        case "DECLINE":
          title = "Your partner left the queue.";
          subtitle = "Try again to match with someone else!";
          break;
        case "CHAT_FAILED":
          title = "Your partner ran into video chat issues.";
          subtitle = "Please go online again when ready.";
          break;
        case "SERVER_ERROR":
          title = "Upduo is currently unavailable.";
          subtitle = "Please try again later!";
          break;
        default:
          title = "A problem was found with your last match.";
          subtitle =
            "No worries -- please go online again whenever you're ready!";
      }
      chatResetState();
      _handleRestoreHome();
      Alert(title, subtitle, [{ text: "Okay", onPress: () => {} }]);
    },
    [_handleRestoreHome]
  );

  const _handleRequestMatchQueue = useCallback(() => {
    // Send the server a request for getting added to queue
    dispatch(ActionCreators.setDisplayedSummitView("Match"));
    setQueueInProgress(true);
    // Post the user to the match queue to be matched with partners
    const deviceId = `web_${md5(window.navigator.appVersion)}`;
    callApi(
      "postMatch",
      {
        onesignal_id: "",
        onesignal_token: "",
        device_id: deviceId,
        is_agora_enabled: true,
      },
      (responseJson: {
        title?: string;
        message?: string;
        outdated?: boolean;
        ignore?: boolean;
      }) => {
        if (responseJson?.ignore) {
          // Server indicates no client action should happen
          // (already in a session, idempotency, etc.)
          return;
        }
        if (responseJson.title && responseJson.message) {
          // JSON contains message string, signifying closed queue
          if (responseJson.outdated) {
            _handleQueueFailedOutdated(responseJson);
          } else {
            _handleQueueFailedClosed(responseJson);
          }
        } else {
          // Inside scheduled time block for Skale sessions
          UpduoAnalytics.logEvent("app-queued");
          setQueueCheckCount(1);
        }
        setQueueInProgress(false);
        setPostProcessingInProgress(false);
      },
      () => {
        if (!chatConnected) {
          setQueueInProgress(false);
          setPostProcessingInProgress(false);
          _handleRestoreHome();
          _handleDisplaySimpleAlert("SERVER_ERROR");
        }
      }
    );
  }, [
    dispatch,
    _handleQueueFailedOutdated,
    _handleQueueFailedClosed,
    _handleRestoreHome,
    _handleDisplaySimpleAlert,
    chatConnected,
  ]);

  const _handlePrepareForMatchQueue = useCallback(() => {
    // Request server checking for availability time window and app version
    callApi(
      "getCheckAppVersion",
      undefined,
      (responseJson: {
        title?: string;
        message?: string;
        outdated?: boolean;
        ignore?: boolean;
      }) => {
        if (responseJson?.ignore) {
          // Server indicates no client action should happen
          // (already in a session, idempotency, etc.)
          return;
        }
        if (responseJson.title && responseJson.message) {
          // JSON contains message string, signifying closed queue
          _handleQueueFailedOutdated(responseJson);
          return;
        }
        callApi(
          "getCheckTimeWindow",
          undefined,
          (timeResponseJson: { title?: string; message?: string }) => {
            if (timeResponseJson.title && timeResponseJson.message) {
              _handleQueueFailedClosed(timeResponseJson);
              return;
            }
            requestSessionPermissions(
              (checkPasses: boolean) => {
                // If status is IN_QUEUE, then alreadyInQueue = true
                // (passed down from getMatchStatus API call)
                if (checkPasses && !alreadyInQueue) {
                  _handleRequestMatchQueue();
                } else if (!alreadyInQueue) {
                  // Don't allow a match if they don't have session permissions
                  dispatch(ActionCreators.setDisplayedSummitView("Summit"));
                  _handleResetQueue();
                }
                setAlreadyInQueue(false);
              },
              () => {
                setStartSessionButtonPressed(false);
                history.push("/");
              }
            );
          },
          _handleFailedMatchApi
        );
      },
      _handleFailedMatchApi
    );
  }, [
    alreadyInQueue,
    _handleFailedMatchApi,
    _handleQueueFailedClosed,
    _handleQueueFailedOutdated,
    _handleRequestMatchQueue,
    _handleResetQueue,
    dispatch,
  ]);

  const _handleSetInactiveDevice = useCallback(() => {
    _handleResetQueue();
    dispatch(ActionCreators.setDisplayedSummitView("DeviceSwitch"));
  }, [_handleResetQueue, dispatch]);

  const _handleMatchStatusQueueCheck = useCallback(() => {
    // Request match status/state from server,
    // to catch missed PubNub events (match-ack and match-found) or unqueued state from server
    if (authToken && !matchStatusInProgress) {
      setMatchStatusInProgress(true);
      const deviceId = `web_${md5(window.navigator.appVersion)}`;
      // Get user's match status
      callApi(
        "getMatchStatus",
        undefined,
        (responseJson) => {
          switch (responseJson.message) {
            case "OTHER_ACTIVE_DEVICE":
              _handleSetInactiveDevice();
              break;
            case "NOT_IN_QUEUE":
              _handleResetQueue();
              if (displayedSummitView !== "Summit") {
                _handleDisplaySimpleAlert("OTHER");
              }
              break;
            case "NEED_ACK":
              _handleMatchAckResponse(responseJson.ack_id);
              break;
            case "MATCH_FOUND":
              _handleMatchFoundResponse(responseJson);
              break;
            default:
          }
          setMatchStatusInProgress(false);
        },
        undefined,
        undefined,
        `?${qs.stringify({
          device_id: deviceId,
        })}`
      );
    } else {
      setPostProcessingInProgress(false);
    }
  }, [
    matchStatusInProgress,
    displayedSummitView,
    _handleDisplaySimpleAlert,
    _handleMatchAckResponse,
    _handleMatchFoundResponse,
    _handleResetQueue,
    _handleSetInactiveDevice,
    authToken,
  ]);

  const _handleMatchStatus = useCallback(() => {
    // Request match status/state from server, respond to any possible state
    if (isQueueBeaconActivated) {
      setStartSessionButtonPressed(true);
      setPreProcessingInProgress(false);
      _handleStartSessionButtonPress();
    } else if (authToken && !matchStatusInProgress) {
      setMatchStatusInProgress(true);
      const deviceId = `web_${md5(window.navigator.appVersion)}`;
      // Get user's match status
      callApi(
        "getMatchStatus",
        undefined,
        (responseJson) => {
          setQueueInProgress(false);
          switch (responseJson.message) {
            case "OTHER_ACTIVE_DEVICE":
              _handleSetInactiveDevice();
              break;
            case "RECEIVED_ACK":
              // Set up listeners and wait for match-found PubNub event
              dispatch(ActionCreators.setDisplayedSummitView("Match"));
              setMatchStatusObject({ request: "other" });
              break;
            case "NEED_ACK":
              // Send ack to server after setting up listeners
              // (match-found PubNub event will arrive soon after)
              dispatch(ActionCreators.setDisplayedSummitView("Match"));
              setMatchStatusObject({
                request: "match-ack",
                payload: responseJson,
              });
              break;
            case "IN_QUEUE":
              // Set up listeners to align app with server's queued state
              setMatchStatusObject({ request: "other" });
              setStartSessionButtonPressed(true);
              // Reset gradient animation and toggle ability
              dispatch(ActionCreators.setMatchAck(false));
              dispatch(ActionCreators.setDisplayedSummitView("Match"));
              setPreProcessingInProgress(false);
              setAlreadyInQueue(true);
              setQueueCheckCount(1);
              break;
            case "MATCH_FOUND":
              // After PubNub match-found, but before the first PubNub match-ready event
              dispatch(ActionCreators.setDisplayedSummitView("Match"));
              setMatchStatusObject({
                request: "match-found",
                payload: responseJson,
              });
              break;
            default:
              // Basically handles 'NOT_IN_QUEUE' case
              _handleNotInQueue();
          }
          setMatchStatusInProgress(false);
        },
        undefined,
        undefined,
        `?${qs.stringify({
          device_id: deviceId,
        })}`
      );
    } else {
      setPreProcessingInProgress(false);
    }
  }, [
    isQueueBeaconActivated,
    matchStatusInProgress,
    _handleNotInQueue,
    _handleSetInactiveDevice,
    _handleStartSessionButtonPress,
    dispatch,
    authToken,
  ]);

  const _handleSetActiveDevice = () => {
    const deviceId = `web_${md5(window.navigator.appVersion)}`;
    UpduoAnalytics.logEvent("user-device-switch", {
      device_id: deviceId,
    });
    callApi("postActiveDevice", { device_id: deviceId }, () => {
      dispatch(ActionCreators.setDisplayedSummitView("Summit"));
      _handleMatchStatus();
    });
  };

  if (authToken && selfObject.organizationCode) {
    // Need to support both selectedSummit.organization.org_code and selectedSummit.organization.orgCode for now
    // (due to use of getSummitDetailWithCode REST API)
    if (
      displayedSummitView === "Summit" &&
      (!selectedSummit?.id ||
        selfObject.organizationCode ===
          selectedSummit?.organization["org_code"] ||
        selfObject.organizationCode === selectedSummit?.organization?.orgCode)
    ) {
      return (
        <Summit
          preProcessingInProgress={preProcessingInProgress}
          startSessionButtonDisabled={
            postProcessingInProgress || startSessionButtonPressed
          }
          startSessionButtonPressed={startSessionButtonPressed}
          onPress={() => {
            setStartSessionButtonPressed(true);
            _handleStartSessionButtonPress();
          }}
          onBackPress={() => {
            UpduoAnalytics.logEvent("user-summit-back");
            history.push("/");
          }}
          inviteUuid={match.params.inviteUuid}
          hashedId={match.params.hashedId}
        />
      );
    }
    if (displayedSummitView === "Match") {
      return (
        <Match
          onCancel={_handleUserMatchCancel}
          queueInProgress={queueInProgress}
          onMatchStatusQueueCheck={_handleMatchStatusQueueCheck}
        />
      );
    } else if (displayedSummitView === "Chat") {
      return <Chat />;
    } else if (displayedSummitView === "SessionFinish") {
      return (
        <SessionFinish
          onFinish={() => {
            _handleRestoreHome();
            history.push("/");
          }}
        />
      );
    } else if (displayedSummitView === "DeviceSwitch") {
      return <DeviceSwitch onSwitch={_handleSetActiveDevice} />;
    }
  }

  // TODO: this should be cleaned up
  const orgCode =
    //@ts-ignore
    selectedSummit?.organization?.org_code !==
    selectedSummit.organization?.domain
      ? //@ts-ignore
        selectedSummit?.organization?.org_code
      : selectedSummit?.organization?.orgCode !==
        selectedSummit.organization?.domain
      ? selectedSummit?.organization?.orgCode
      : "";
  return (
    <Summit
      preProcessingInProgress={preProcessingInProgress}
      onPress={() => {
        if (primaryAuthToken) {
          if (orgCode) {
            primaryJoinOrg({
              variables: {
                primaryJoinOrgRequest: {
                  orgCode,
                },
              },
            });
          }
        } else {
          history.push({
            pathname: `/start/${orgCode}`,
            state: {
              referrer: {
                pathname: history.location.pathname,
                redirect: true,
                rsvpRequest: match.params.inviteUuid ? true : false,
              },
            },
          });
        }
      }}
      onBackPress={() => {
        UpduoAnalytics.logEvent("user-summit-back");
        history.push("/");
      }}
      inviteUuid={match.params.inviteUuid}
      hashedId={match.params.hashedId}
    />
  );
}
